/**
 * Project Facade (100% Shared)
 * 
 * Wraps all project-related Redux state and actions.
 * This facade is completely platform-agnostic and can be used
 * by both web and plugin without any modifications.
 * 
 * Usage:
 * ```tsx
 * const project = useProjectFacade();
 * const { projects, isLoading } = project;
 * await project.create({ name: 'My Project' });
 * ```
 * 
 * @module useProjectFacade
 * @category Facades
 */

import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  getUserProjectsWithPagination,
  createProject,
  deleteProject,
  updateProject,
  getProject,
  setProjectMetaData,
  setMetadataConversationId,
  selectProjects,
  selectIsLoadingProjects,
  selectProjectMetaData,
  selectMetadataConversationId,
} from '../../../store/slices/projectSlice';
import {
  setCurrentProjectId,
  setShowProjectDetailsDrawer,
  selectCurrentProjectId,
} from '../../../store/slices/appSlice';
import { projectService } from '../../../api/services/project.service';

export interface ProjectFacade {
  // ========== State ==========
  /**
   * List of user projects
   */
  projects: any[];

  /**
   * Loading state for projects list
   */
  isLoading: boolean;

  /**
   * Currently selected project ID
   */
  currentProjectId: string | null;

  /**
   * Current project metadata
   */
  projectMetaData: any;

  // ========== Actions ==========
  /**
   * Get paginated list of user projects
   * @param params - Pagination parameters
   */
  getList: (params: { limit: number; page: number }) => Promise<any>;

  /**
   * Get a single project by ID
   * @param id - Project ID
   */
  getById: (id: string) => Promise<any>;

  /**
   * Create a new project
   * @param data - Project creation data
   */
  create: (data: { name: string; description?: string }) => Promise<any>;

  /**
   * Update an existing project
   * @param id - Project ID
   * @param data - Project update data
   */
  update: (id: string, data: any) => Promise<any>;

  /**
   * Delete a project
   * @param id - Project ID
   */
  delete: (id: string) => Promise<any>;

  /**
   * Set current project ID
   * @param id - Project ID
   */
  setCurrent: (id: string) => void;

  /**
   * Set project metadata
   * @param data - Metadata object
   */
  setMetadata: (data: any) => void;

  /**
   * Get project metadata from AI
   * @param params - Conversation ID and/or user message
   */
  getMetadata: (params: { conversationId?: string; userMessage?: string }) => Promise<any>;

  /**
   * Set project details drawer visibility
   * @param show - Whether to show drawer
   */
  setDrawerVisibility: (show: boolean) => void;
}

/**
 * Project Facade Hook
 * 
 * Provides project management capabilities.
 * 100% shared between web and plugin.
 */
export function useProjectFacade(): ProjectFacade {
  const dispatch = useAppDispatch();

  // State
  const projects = useAppSelector(selectProjects);
  const isLoading = useAppSelector(selectIsLoadingProjects);
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const projectMetaData = useAppSelector(selectProjectMetaData);
  const metadataConversationId = useAppSelector(selectMetadataConversationId);

  return {
    // State
    projects,
    isLoading,
    currentProjectId,
    projectMetaData,

    // Actions
    getList: useCallback(
      (params: { limit: number; page: number }) =>
        dispatch(getUserProjectsWithPagination(params)),
      [dispatch]
    ),

    getById: useCallback(
      (id: string) => dispatch(getProject(id)),
      [dispatch]
    ),

    create: useCallback(
      (data: { 
        name: string; 
        description?: string;
        tmp_conversation_id?: string;
        ideoz_role?: string;
        main_persona?: string;
        industry?: string;
      }) => {
        return dispatch(createProject(data));
      },
      [dispatch]
    ),

    update: useCallback(
      (id: string, data: any) =>
        dispatch(updateProject({ projectId: id, projectData: data })),
      [dispatch]
    ),

    delete: useCallback(
      (id: string) => dispatch(deleteProject(id)),
      [dispatch]
    ),

    setCurrent: useCallback(
      (id: string) => dispatch(setCurrentProjectId(id)),
      [dispatch]
    ),

    setMetadata: useCallback(
      (data: any) => dispatch(setProjectMetaData(data)),
      [dispatch]
    ),

    getMetadata: useCallback(
      async (params: { conversationId?: string; userMessage?: string }) => {
        // Check if we have cached metadata for the same conversation ID
        if (params.conversationId && 
            metadataConversationId === params.conversationId && 
            projectMetaData) {
          // Return cached metadata
          return {
            metaData: projectMetaData,
            conversationId: params.conversationId,
            isCached: true
          };
        }

        try {
          const metadata = await projectService.getProjectMetadata({
            tmp_conversation_id: params.conversationId,
            user_message: params.userMessage,
          });
          // Update metadata and store the conversation ID for caching
          dispatch(setProjectMetaData(metadata));
          if (params.conversationId) {
            dispatch(setMetadataConversationId(params.conversationId));
          }
          
          return {
            metaData: metadata,
            conversationId: params.conversationId,
            isCached: false
          };
        } catch (error) {
          console.error('Failed to get project metadata:', error);
          throw error;
        }
      },
      [dispatch, metadataConversationId, projectMetaData]
    ),

    setDrawerVisibility: useCallback(
      (show: boolean) => dispatch(setShowProjectDetailsDrawer(show)),
      [dispatch]
    ),
  };
}
